import type { InspectionLine } from '@obraplan/core/application/use-cases/planning/inspection-use-cases';
import type { DayQualityWorkday } from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import type { OutboxEntry } from '@/app/outbox-types';
import { comAFila } from './fila-da-conferencia';

/**
 * A jornada do dia com o que a fila do aparelho ainda vai gravar (Fase 43, RN-641).
 *
 * A hora extra do lançamento é a conta do servidor (RN-045): as horas que a pessoa já tem no
 * dia, mais as lançadas. Sem sinal, as horas já tem uma parte que o servidor ainda não viu — o
 * lançamento de antes, o ajuste, a frente tirada —, e a fila sobe na ordem, antes do
 * lançamento novo (RN-605). Sem somá-la, o formulário não pedia o motivo, e o servidor recusava
 * na volta, quando já não havia onde escrevê-lo.
 */
export interface JornadaDoDia extends DayQualityWorkday {
  /** O quanto das horas do dia ainda espera na fila. Ausente quando nada espera. */
  readonly queuedQuarterHours?: number;
}

/**
 * As jornadas do servidor com a fila por cima.
 *
 * - **O lançamento** (pessoa e equipe) cria alocação nova: soma as horas de cada um.
 * - **O ajuste e o "Tirar"** mudam alocação que o servidor já tem. A diferença sai do cartão
 *   com a fila aplicada (`comAFila`), a mesma previsão que a tela mostra — por isso as linhas
 *   do dia, quando a tela as tem.
 *
 * O recusado conta junto: ele ainda está no aparelho, e "Tentar de novo" o manda na mesma
 * ordem. Pedir o motivo a mais não custa nada — o servidor só o lê acima da jornada.
 */
export function jornadasComAFila(
  workdays: readonly DayQualityWorkday[],
  entries: readonly OutboxEntry[],
  linhas: readonly InspectionLine[] = [],
): readonly JornadaDoDia[] {
  const naFila = new Map(horasLancadasNaFila(entries));
  const somar = (resourceId: string, quartos: number): void => {
    if (quartos === 0) return;
    naFila.set(resourceId, (naFila.get(resourceId) ?? 0) + quartos);
  };

  for (const linha of linhas) {
    const daPessoa = entries.filter(
      (entry) => entry.operation.resourceId === linha.resource.resourceId,
    );
    if (daPessoa.length === 0) continue;
    const antes = totalDe(linha);
    somar(linha.resource.resourceId, totalDe(comAFila(linha, daPessoa)) - antes);
  }

  if (naFila.size === 0) return workdays;
  return workdays.map((dia) => {
    const quartos = naFila.get(dia.resourceId);
    if (quartos === undefined) return dia;
    return {
      ...dia,
      allocatedQuarterHours: Math.max(0, dia.allocatedQuarterHours + quartos),
      queuedQuarterHours: quartos,
    };
  });
}

/**
 * As horas que os lançamentos da fila ainda vão criar, por pessoa (RN-641, RN-642).
 *
 * É a parte da fila que o cartão não mostra: o lançamento cria alocação nova no servidor, e o
 * cartão só ganha a marca de que algo espera (`comAFila`). O ajuste do cartão soma estas horas
 * às das frentes dele para saber se o dia passa da jornada.
 */
export function horasLancadasNaFila(entries: readonly OutboxEntry[]): ReadonlyMap<string, number> {
  const horas = new Map<string, number>();
  const somar = (resourceId: string, quartos: number): void => {
    if (quartos === 0) return;
    horas.set(resourceId, (horas.get(resourceId) ?? 0) + quartos);
  };
  for (const { operation } of entries) {
    if (operation.kind === 'lancar-trabalho') {
      somar(operation.input.resourceId, operation.input.quarterHours);
    } else if (operation.kind === 'lancar-trabalho-equipe') {
      for (const membro of operation.input.members) somar(membro.resourceId, membro.quarterHours);
    }
  }
  return horas;
}

function totalDe(linha: InspectionLine): number {
  return linha.allocations.reduce((soma, item) => soma + item.allocation.quarterHours, 0);
}
